// Top-level lanes used to group care blocks in the dashboard UI.
export const BLOCK_TYPE = {
  MindGut: 'mind-gut',
  BehaviorChange: 'behavior change',
  Clinical: 'clinical',
  Nutrition: 'nutrition',
  Visit: 'visit',
} as const;

export type BlockType = typeof BLOCK_TYPE[keyof typeof BLOCK_TYPE];

// Known archetypes for dashboard block templates.
export enum BlockArchetype {
  Goal = 'goal',
  Program = 'program',
  Task = 'task',
}

// Known nutrition program subtypes inferred from nutrition block names.
export enum NutritionBlockType {
  EliminationChallenge = 'elimination-challenge',
  FoodChallenge = 'food-challenge',
  IdentifySuspectTriggerFoods = 'identify-suspect-trigger-foods',
  Unknown = 'unknown',
}

// Coach/provider identity embedded in `UserBlock.assigned_by`.
export type UserBlockAssignee = {
  id: number;
  full_name: string;
  display_name: string;
  healthie_id: number;
  image_url: string;
  type: string;
  care_member_type: string | null;
  uuid: string;
};

// User-specific assigned block instance returned by `/private/user/blocks/`.
// This is the block as attached to a member (dates, assignee, lifecycle).
export type UserBlock = {
  id: string;
  assigned_by: UserBlockAssignee | null;
  block_name: string;
  block_type: BlockType;
  date_assigned: string | null;
  date_ended: string | null;
  date_archived: string | null;
  instructions: string | null;
  archetype: BlockArchetype | null;
  metadata: {
    /** Count of gut checks logged by the patient against this block. */
    logged_gut_checks?: number | null;
    /**
     * Total gut checks prescribed for this block (e.g. 21 for a 3-week
     * Elimination Challenge). Drives EngagementAddon of-total mode.
     */
    gut_checks_total?: number | null;
  };
  stable_string: string | null;
};

// Dashboard block catalog item returned by `/private/dashboard/blocks/`.
// This is the assignable block definition (template), not user-assigned state.
export type DashboardBlock = {
  id: string;
  name: string;
  block_type: BlockType;
  archetype: BlockArchetype | null;
  stable_string: string | null;
};

export const BLOCK_STABLE_STRINGS = {
  identifySuspects: 'identify_suspects',
  eliminationChallenge: 'elimination_challenge',
  foodChallenge: 'food_challenge',
};

export type BlockStableStrings =
  typeof BLOCK_STABLE_STRINGS[keyof typeof BLOCK_STABLE_STRINGS];

export const ADJUSTABLE_NUTRITION_BLOCKS = [
  BLOCK_STABLE_STRINGS.eliminationChallenge,
  BLOCK_STABLE_STRINGS.foodChallenge,
  BLOCK_STABLE_STRINGS.identifySuspects,
] as const;
